import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { startAuthentication } from '@simplewebauthn/browser'
import { useOptimistic, useState, useTransition } from 'react'
import {
	data,
	Link,
	type ShouldRevalidateFunction,
	useNavigate,
	useSearchParams,
} from 'react-router'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	AuthButton,
	AuthDivider,
	AuthErrors,
	AuthFootnote,
	AuthShell,
} from '#app/components/heute/auth-shell.tsx'
import {
	CodeStap,
	NummerVeld,
	UitnodigingStand,
} from '#app/components/heute/telefoon-deur.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import {
	ProviderConnectionForm,
	providerNames,
} from '#app/utils/connections.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getTranslator } from '#app/utils/i18n.server.ts'
import { useT } from '#app/utils/i18n.tsx'
import { getErrorMessage } from '#app/utils/misc.tsx'
import { type Route } from './+types/login.ts'
import {
	deurActie,
	vergeetBewijs,
	type DeurStand,
} from './telefoon-deur.server.ts'

/**
 * Inloggen (W2): je nummer, de code uit de sms, klaar. Een nummer zonder
 * account gaat vanzelf door naar je naam (aanmelden, achter de bètapoort).
 * Daaronder rustig de andere wegen: een toegangssleutel, en inloggen met
 * een e-mailcode als je je telefoon niet bij de hand hebt.
 */

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const AuthenticationOptionsSchema = z.object({
	options: z.object({ challenge: z.string() }),
}) satisfies z.ZodType<{ options: PublicKeyCredentialRequestOptionsJSON }>

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const t = await getTranslator(request)
	return data(
		{ metaTitle: `${t('auth.meta_title_login')} | heute` },
		{ headers: await vergeetBewijs(request) },
	)
}

/** Het bewijs dat de action net zette, mag de revalidatie erna niet wissen
 *  (de loader vergeet het bewijs; de pagina zelf leest niets dat verandert). */
export const shouldRevalidate: ShouldRevalidateFunction = ({
	formMethod,
	defaultShouldRevalidate,
}) => (formMethod && formMethod !== 'GET' ? false : defaultShouldRevalidate)

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const redirectTo = formData.get('redirectTo')
	return deurActie(request, formData, {
		redirectTo:
			typeof redirectTo === 'string' && redirectTo ? redirectTo : undefined,
	})
}

export default function LoginPage({ actionData }: Route.ComponentProps) {
	const t = useT()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const stand = (actionData as DeurStand | undefined) ?? { stap: 'nummer' }
	const verborgen = redirectTo ? (
		<input type="hidden" name="redirectTo" value={redirectTo} />
	) : null
	const metDoel = (pad: string) =>
		redirectTo ? `${pad}?${new URLSearchParams({ redirectTo })}` : pad

	if (stand.stap === 'uitnodiging') {
		return (
			<UitnodigingStand
				onbekendNummer
				redirectTo={redirectTo}
				houd={stand.houd}
			/>
		)
	}
	if (stand.stap === 'code') {
		return (
			<CodeStap stand={stand} cta={t('auth.login_cta')}>
				{verborgen}
			</CodeStap>
		)
	}

	return (
		<AuthShell title={t('auth.login_title')} sub={t('auth.tel_login_sub')}>
			<NummerVeld
				fout={stand.fout}
				landcode={stand.landcode}
				nummer={stand.nummer}
				cta={t('auth.send_code')}
			>
				{verborgen}
			</NummerVeld>
			<AuthDivider />
			<PasskeyLogin redirectTo={redirectTo} remember />
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 10,
					marginTop: 10,
				}}
			>
				{providerNames.map((providerName) => (
					<ProviderConnectionForm
						key={providerName}
						type="Login"
						providerName={providerName}
						redirectTo={redirectTo}
					/>
				))}
			</div>
			<div style={{ textAlign: 'center', marginTop: 12 }}>
				<Link
					to={metDoel('/login/email')}
					className="tekst-link"
					data-testid="login-via-email"
					style={{ color: 'var(--ink-2)' }}
				>
					{t('auth.via_email')}
				</Link>
			</div>
			<AuthFootnote>
				{t('auth.new_here')}{' '}
				<Link to={metDoel('/signup')}>{t('auth.create_account')}</Link>
			</AuthFootnote>
		</AuthShell>
	)
}

const VerificationResponseSchema = z.discriminatedUnion('status', [
	z.object({
		status: z.literal('success'),
		location: z.string(),
	}),
	z.object({
		status: z.literal('error'),
		error: z.string(),
	}),
])

function PasskeyLogin({
	redirectTo,
	remember,
}: {
	redirectTo: string | null
	remember: boolean
}) {
	const t = useT()
	const [isPending] = useTransition()
	const [error, setError] = useState<string | null>(null)
	const [passkeyMessage, setPasskeyMessage] = useOptimistic<string | null>(
		t('auth.passkey_login'),
	)
	const navigate = useNavigate()

	async function handlePasskeyLogin() {
		try {
			setPasskeyMessage(t('auth.passkey_generating'))
			// Get authentication options from the server
			const optionsResponse = await fetch('/webauthn/authentication')
			const json = await optionsResponse.json()
			const { options } = AuthenticationOptionsSchema.parse(json)

			setPasskeyMessage(t('auth.passkey_authorizing'))
			const authResponse = await startAuthentication({ optionsJSON: options })
			setPasskeyMessage(t('auth.passkey_verifying'))

			// Verify the authentication with the server
			const verificationResponse = await fetch('/webauthn/authentication', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ authResponse, remember, redirectTo }),
			})

			const verificationJson = await verificationResponse.json().catch(() => ({
				status: 'error',
				error: 'Unknown error',
			}))

			const parsedResult =
				VerificationResponseSchema.safeParse(verificationJson)
			if (!parsedResult.success) {
				throw new Error(parsedResult.error.message)
			} else if (parsedResult.data.status === 'error') {
				throw new Error(parsedResult.data.error)
			}
			const { location } = parsedResult.data

			setPasskeyMessage(t('auth.passkey_success'))
			await navigate(location ?? '/')
		} catch (e) {
			const errorMessage = getErrorMessage(e)
			setError(t('auth.passkey_failed', { error: errorMessage }))
		}
	}

	return (
		<form action={handlePasskeyLogin}>
			<AuthButton
				id="passkey-login-button"
				aria-describedby="passkey-login-button-error"
				tone="ghost"
				type="submit"
				pending={isPending}
				style={{ gap: 8 }}
			>
				<Icon name="passkey" />
				{passkeyMessage}
			</AuthButton>
			<AuthErrors errors={[error]} id="passkey-login-button-error" />
		</form>
	)
}

export const meta: Route.MetaFunction = ({ data: d }) => {
	return [{ title: d ? d.metaTitle : 'heute' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
